<template>
  <div>
    <!-- Header (overall) -->
    <div class="d-flex align-center justify-space-between mb-2">
      <div class="text-subtitle-1 font-weight-bold">{{ computedTitle }}</div>

      <div class="d-flex align-center">
        <v-chip
          x-small
          class="ml-2"
          :color="totalLeft === 0 ? 'grey' : 'green'"
          text-color="white"
        >
          {{ totalLeft }} left
        </v-chip>

        <!-- <v-btn
          small
          rounded
          color="success"
          :disabled="totalLeft === 0"
          class="ml-2"
          @click="$emit('donate')"
        >
          {{ totalLeft === 0 ? "Completed" : "Sponsor" }}
        </v-btn> -->
      </div>
    </div>

    <div
      class="text-body-2 grey--text text--darken-1 mb-3"
      v-if="computedSubtitle"
    >
      {{ computedSubtitle }}
    </div>

    <div
      v-if="!categories.length"
      class="text-body-2 grey--text text--darken-1"
    >
      No sponsorship spots found.
    </div>

    <!-- Categories -->
    <div v-for="cat in categories" :key="cat.key" class="cat-card">
      <div class="cat-head">
        <div class="cat-title">{{ cat.name }}</div>

        <div class="d-flex align-center">
          <v-chip
            x-small
            :color="cat.left === 0 ? 'grey' : 'green'"
            text-color="white"
          >
            {{ cat.left }} left
          </v-chip>

          <v-chip
            v-if="cat.left === 0"
            x-small
            class="ml-2"
            color="grey"
            text-color="white"
          >
            Completed
          </v-chip>
        </div>
      </div>

      <!-- Items inside category -->
      <div v-for="item in cat.items" :key="item.key" class="item-row">
        <div class="item-top">
          <div class="item-name">
            {{ item.name || "Item" }}
          </div>

          <div class="item-meta">
            <span class="price" v-if="item.amount > 0">
              {{ money(displayMonthlyPrice(item.amount)) }}
            </span>
            <span class="dot">•</span>
            <span class="muted">per month</span>
            <span class="left" :class="{ zero: item.left === 0 }">
              {{ item.left }} left
            </span>
          </div>
        </div>

        <!-- Seats preview -->
        <div class="seat-grid">
          <div
            v-for="seat in buildPreviewSeats(item.seats)"
            :key="seat.key"
            class="seat"
            :class="{
              taken: seat.taken,
              clickable: seat.isMore || !seat.taken,
              more: seat.isMore,
            }"
            role="button"
            tabindex="0"
            :title="seat.label"
            @click="onSeatClick(seat)"
            @keydown.enter.prevent="onSeatClick(seat)"
            @keydown.space.prevent="onSeatClick(seat)"
          >
            <template v-if="seat.isMore">
              <div class="more-text">+{{ seat.count }}</div>
            </template>

            <template v-else>
              <v-icon v-if="seat.taken" small class="seat-check">
                mdi-check
              </v-icon>

              <v-icon v-else small class="seat-icon">
                {{ seat.icon || "mdi-account" }}
              </v-icon>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer summary -->
    <div class="d-flex align-center justify-space-between mt-3">
      <div class="text-caption grey--text text--darken-1">
        Total: <strong>{{ totalSeats }}</strong
        >, Taken: <strong>{{ totalTaken }}</strong
        >, Left: <strong>{{ totalLeft }}</strong>
      </div>

      <v-btn
        small
        color="success"
        rounded
        :disabled="totalLeft === 0"
        @click="$emit('donate')"
      >
        {{ totalLeft === 0 ? "Completed" : "Sponsor now" }}
      </v-btn>
    </div>
  </div>
</template>

<script>
const MAX_PREVIEW = 15;

export default {
  name: "SponsorSeatGrid",
  props: {
    details: { type: Array, default: () => [] },
    title: { type: String, default: "" },
    subtitle: { type: String, default: "" },
    kind: { type: String, default: "student" }, // student | unit | fast
    durationType: { type: String, default: "" }, // "yearly" | "monthly" (optional)
  },
  computed: {
    isYearlySponsor() {
      // Only apply to student sponsorship (not units)
      if (this.normalizedKind === "unit") return false;

      const dt = (this.durationType || "").toString().toLowerCase().trim();

      // If parent passes it, trust it
      if (dt) return dt === "yearly";

      // Fallback: sponsor campaigns are yearly by default
      return true;
    },
    normalizedKind() {
      const k = (this.kind || "").toString().toLowerCase().trim();
      if (k === "units") return "unit";
      return k || "student";
    },

    computedTitle() {
      if (this.title) return this.title;
      if (this.normalizedKind === "unit") return "Unit sponsorship";
      if (this.normalizedKind === "fast") return "Sponsor a day";
      return "Student sponsorship";
    },

    computedSubtitle() {
      if (this.subtitle) return this.subtitle;
      if (this.normalizedKind === "unit")
        return "Pick units to sponsor. Taken units are marked.";
      if (this.normalizedKind === "fast")
        return "Pick a day to sponsor. Taken days are marked.";
      return "Pick a student to sponsor. Taken spots are crossed out.";
    },
    categories() {
      const rows = Array.isArray(this.details) ? this.details : [];

      // Map(category -> Map(itemName -> item))
      const byCat = new Map();

      rows.forEach((d, idx) => {
        if (!d) return;

        const category = (d.category || d.Category || "General")
          .toString()
          .trim();
        const name = (d.name || d.title || d.studentName || "Item")
          .toString()
          .trim();

        const amount = Number(d.amount ?? d.price ?? d.goalAmount ?? 0) || 0;

        // capacity fallback to 1 (supports capacity/qty/quantity)
        const capRaw = d.capacity ?? d.qty ?? d.quantity ?? 1;
        const capNum =
          capRaw === null || capRaw === undefined || capRaw === ""
            ? 1
            : Number(capRaw) || 0;
        const capacity = capNum > 0 ? capNum : 1;

        // Backward compatible "taken" logic
        // const sold = Number(d.soldQty ?? d.sold ?? 0) || 0;
        const received =
          Number(d.received ?? d.collectedAmount ?? d.raised ?? 0) || 0;

        const remainingQty =
          d.remainingQty === null || d.remainingQty === undefined
            ? null
            : Number(d.remainingQty);

        // compute taken from multiple signals, then take the strongest one
        const takenFromSold = Math.min(
          capacity,
          Math.max(0, Number(d.soldQty ?? d.sold ?? 0) || 0)
        );

        const takenFromRemaining =
          remainingQty !== null && !isNaN(remainingQty)
            ? Math.min(
                capacity,
                Math.max(0, capacity - Math.max(0, remainingQty))
              )
            : 0;

        const takenFromReceived =
          amount > 0 && received > 0
            ? Math.min(capacity, Math.max(0, Math.floor(received / amount)))
            : 0;

        // pick the max so if one field is wrong/missing, UI still marks taken seats
        const takenCount = Math.max(
          takenFromSold,
          takenFromRemaining,
          takenFromReceived
        );

        // Build seats for this row
        const seats = [];
        const icon = this.pickIcon(name, category);

        for (let i = 0; i < capacity; i++) {
          const taken = i < takenCount;
          seats.push({
            key: `${d.id || idx}-${i}`,
            taken,
            icon, // ✅ add this
            label: `${name} (${taken ? "Taken" : "Available"})`,
          });
        }
        if (!byCat.has(category)) byCat.set(category, new Map());
        const byItem = byCat.get(category);

        if (!byItem.has(name)) {
          byItem.set(name, {
            key: `${category}-${name}`,
            name,
            amount,
            seats: [],
          });
        }

        const item = byItem.get(name);
        if (!item.amount && amount) item.amount = amount;
        item.seats = item.seats.concat(seats);
      });

      // Convert to array with totals
      const out = [];
      for (const [catName, itemMap] of byCat.entries()) {
        const items = Array.from(itemMap.values()).map((it) => {
          const taken = it.seats.filter((s) => s.taken).length;
          const left = Math.max(0, it.seats.length - taken);
          return { ...it, taken, left };
        });

        // sort items: available first, then by name
        items.sort((a, b) => {
          if (a.left === 0 && b.left > 0) return 1;
          if (a.left > 0 && b.left === 0) return -1;
          return (a.name || "").localeCompare(b.name || "");
        });

        const total = items.reduce((sum, it) => sum + it.seats.length, 0);
        const taken = items.reduce((sum, it) => sum + it.taken, 0);
        const left = Math.max(0, total - taken);

        out.push({
          key: `cat-${catName}`,
          name: catName,
          total,
          taken,
          left,
          items,
        });
      }

      // sort categories: available first, then by name
      out.sort((a, b) => {
        if (a.left === 0 && b.left > 0) return 1;
        if (a.left > 0 && b.left === 0) return -1;
        return (a.name || "").localeCompare(b.name || "");
      });

      return out;
    },

    totalSeats() {
      return this.categories.reduce((sum, c) => sum + c.total, 0);
    },
    totalTaken() {
      return this.categories.reduce((sum, c) => sum + c.taken, 0);
    },
    totalLeft() {
      return Math.max(0, this.totalSeats - this.totalTaken);
    },
  },

  methods: {
    normalizeText(s) {
      return (s || "").toString().toLowerCase().trim();
    },
    displayMonthlyPrice(amount) {
      const n = Number(amount || 0);
      if (!n) return 0;

      // ✅ units stay as-is (already monthly)
      if (this.normalizedKind === "unit") return n;

      // ✅ yearly sponsor: show monthly
      if (this.isYearlySponsor) return n / 12;

      // monthly sponsor (if you ever make it): keep as-is
      return n;
    },
    pickIcon(itemName, catName) {
      // Only do smart icons for unit mode
      if (this.normalizedKind !== "unit") return "mdi-account";

      const txt = `${this.normalizeText(catName)} ${this.normalizeText(
        itemName
      )}`;

      const rules = [
        {
          keys: ["electric", "electricity", "power", "kwh"],
          icon: "mdi-flash",
        },
        { keys: ["gas", "heat", "heating"], icon: "mdi-fire" },
        { keys: ["water", "aqua", "sewer", "plumbing"], icon: "mdi-water" },
        {
          keys: ["fire alarm", "smoke", "sprinkler"],
          icon: "mdi-smoke-detector",
        },
        {
          keys: ["alarm", "security", "adt", "camera", "cctv"],
          icon: "mdi-shield-home",
        },
        {
          keys: ["security guard", "guard", "patrol"],
          icon: "mdi-account-shield",
        },
        { keys: ["internet", "wifi", "network"], icon: "mdi-wifi" },
        { keys: ["phone", "mobile", "cell"], icon: "mdi-cellphone" },
        { keys: ["rent", "lease"], icon: "mdi-home-city" },
        {
          keys: ["landscape", "landscaping", "lawn", "grass", "snow"],
          icon: "mdi-tree",
        },
        {
          keys: ["insurance", "liability", "policy"],
          icon: "mdi-shield-check",
        },
      ];

      for (const r of rules) {
        if (r.keys.some((k) => txt.includes(k))) return r.icon;
      }

      return "mdi-cube-outline"; // default “unit” vibe
    },
    money(val) {
      const n = Number(val || 0);
      return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
    },

    seatSort(a, b) {
      // available first
      return Number(a.taken) - Number(b.taken);
    },

    buildPreviewSeats(seats) {
      const sorted = Array.isArray(seats) ? [...seats].sort(this.seatSort) : [];
      const total = sorted.length;

      if (total <= MAX_PREVIEW) return sorted;

      // show 14 + "more" tile = 15 total tiles
      const preview = sorted.slice(0, MAX_PREVIEW - 1);
      const remaining = total - preview.length;

      preview.push({
        key: `more-${remaining}`,
        isMore: true,
        count: remaining,
        taken: false,
        label: `+${remaining} more`,
      });

      return preview;
    },

    onSeatClick(seat) {
      // Clicking +more should go to sponsor
      if (seat && seat.isMore) return this.$emit("donate");

      // If taken, do nothing (keeps it clean)
      if (seat && seat.taken) return;

      // Any available seat click acts like Sponsor button
      this.$emit("donate");
    },
  },
};
</script>

<style scoped>
.cat-card {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  padding: 12px;
  background: #fff;
  margin-bottom: 12px;
}

.cat-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.cat-title {
  font-weight: 900;
  font-size: 14px;
}

.item-row {
  padding: 10px 0;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.item-row:first-of-type {
  border-top: 0;
  padding-top: 0;
}

.item-top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
}

.item-name {
  font-weight: 800;
  font-size: 13px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  opacity: 0.9;
  white-space: nowrap;
}

.price {
  font-weight: 800;
}

.dot {
  opacity: 0.6;
}

.left.zero {
  opacity: 0.6;
}

/* seats */
.seat-grid {
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: 8px;
}

@media (max-width: 420px) {
  .seat-grid {
    grid-template-columns: repeat(8, 1fr);
  }
}

.seat {
  position: relative;
  height: 34px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* .seat.taken {
  background: rgba(0, 0, 0, 0.06);
  opacity: 0.75;
} */

.seat-check {
  color: #4caf50;
  font-size: 18px;
  font-weight: 900;
}

.seat.taken {
  background: rgba(76, 175, 80, 0.12); /* soft green */
  border-color: rgba(76, 175, 80, 0.45);
  opacity: 1;
}

.seat-icon {
  color: rgba(0, 0, 0, 0.45);
}

.seat-x {
  position: absolute;
  right: 6px;
  top: 6px;
  opacity: 0.8;
}

.seat.more {
  background: rgba(25, 118, 210, 0.08);
  border-color: rgba(25, 118, 210, 0.25);
}

.more-text {
  font-weight: 900;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.7);
}

.seat.clickable {
  cursor: pointer;
}

.seat.clickable:hover {
  border-color: rgba(25, 118, 210, 0.35);
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.1);
}
</style>